/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2020. All rights reserved
------------------------------------------------------------------------------------------------- */

import { css, html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
/**
 * @prop {String} header - Defines the header text shown on top of the progress bar or progress circle.
 * @prop {String} label - Defines the label text shown on top of the progress bar or inside the progress circle.
 * @prop {String} info - Defines the information text displayed below the progress bar/circle.
 * @prop {String} status - Defines the icon shown before the info text. Possible values are `error`, `warning` and `success`.
 * @prop {String} color - Custom color for progress visualization.
 * @prop {String} size - If radial is set to true, defines the size of the progress circle (xs: 16px, s: 32px, m: 64px, l: 128px, xl: 256px)
 * @prop {Number} value - Default 0. Represents the progress in percentage. Defines the width of the progress bar.
 * @prop {Boolean} indeterminate - Default false. If set to `true`, the progress bar will display a looping animation, and not the exact value.
 * @prop {Boolean} paused - Default false. If set to `true`, the progress bar will change from orange to a neutral color.
 * @prop {String} type - Defines the icon shown before the info text. Possible values are `error`, `warning` and `success`.
 * @slot functions - Takes Icons and Text Links as children. Should be used for actions such as pause, cancel, resume.
 */
export class SHProgressRadial extends LitElement {
  static get properties() {
    return {
      header: { type: String, reflect: true },
      label: { type: String, reflect: true },
      info: { type: String, reflect: true },
      color: { type: String, reflect: true },
      size: { type: String, reflect: true },
      value: { type: Number, reflect: true },
    };
  }

  static get styles() {
    return [sharedStyles, css``];
  }

  render() {
    return html`
      <sh-progress
        radial
        .header="${this.header}"
        .label="${this.label}"
        .info="${this.info}"
        .color="${this.color}"
        .size="${this.size}"
        .value="${this.value}"
      ></sh-progress>
    `;
  }

  updated(changedProperties) {
    const listOfProperties = ['header', 'label', 'info', 'color', 'size', 'value'];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
  }

  constructor() {
    super();
    this.value = 0;
    this.size = 'm';
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'progressbar');
    }
  }
}

customElements.define('sh-progress-radial', SHProgressRadial);
